import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { StyledButton } from '../../Exams/styled';
import { AddSubSkillFormContainer } from '../styled';
import { EditSubSkillModalProps } from '../types';

const EditSubSkillModal: React.FC<EditSubSkillModalProps> = ({
  open,
  onClose,
  onSave,
  subSkill,
  scoringTypeOptions,
  isLoadingScoringTypes
}) => {
  const [description, setDescription] = useState('');
  const [scoringTypeId, setScoringTypeId] = useState('');
  const [order, setOrder] = useState('');
  const [maxMarks, setMaxMarks] = useState('');

  useEffect(() => {
    if (open && subSkill) {
      setDescription(subSkill.description);
      const resolvedId =
        typeof subSkill.scoringType === 'string'
          ? subSkill.scoringType
          : subSkill.scoringType?.id || '';
      setScoringTypeId(resolvedId);
      setOrder(String(subSkill.order));
      setMaxMarks(subSkill.maxMarks != null ? String(subSkill.maxMarks) : '');
    }
  }, [open, subSkill]);

  const selectedScoringType = scoringTypeOptions.find(
    (st) => st.id === scoringTypeId
  );
  const showMaxMarks =
    !!scoringTypeId &&
    (!selectedScoringType?.validScores ||
      selectedScoringType.validScores.length === 0);

  useEffect(() => {
    if (!showMaxMarks) {
      setMaxMarks('');
    }
  }, [showMaxMarks]);

  const handleScoringTypeChange = (value: string) => {
    setScoringTypeId(value);
    const st = scoringTypeOptions.find((s) => s.id === value);
    const willShowMaxMarks =
      !!value && (!st?.validScores || st.validScores.length === 0);
    if (!willShowMaxMarks) setMaxMarks('');
  };

  const handleSave = () => {
    if (!subSkill) return;

    const scoringType = scoringTypeOptions.find(
      (st) => st.id === scoringTypeId
    );
    const resolvedScoringType = scoringType
      ? {
          id: scoringType.id!,
          name: scoringType.name,
          validScores: scoringType.validScores || []
        }
      : scoringTypeId;

    onSave({
      ...subSkill,
      description,
      scoringType: resolvedScoringType,
      order: Number(order) || 0,
      maxMarks: maxMarks !== '' ? Number(maxMarks) : undefined
    });
    onClose();
  };

  const isUpdateDisabled =
    !description || !scoringTypeId || !order || (showMaxMarks && !maxMarks);

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Edit Sub-Skill'
      actions={
        <>
          <Button onClick={onClose} color='primary' variant='outlined'>
            Cancel
          </Button>
          <StyledButton onClick={handleSave} disabled={isUpdateDisabled}>
            Update
          </StyledButton>
        </>
      }
    >
      <AddSubSkillFormContainer>
        <TextInputWithIcon
          label='Description'
          placeholder='Enter Description'
          required
          name='description'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
        />
        <DropdownSelect
          label='Scoring Type'
          placeholder='Select Scoring Type'
          required
          name='scoringType'
          value={scoringTypeId}
          options={scoringTypeOptions.map((st) => ({
            value: st.id || '',
            label: st.name
          }))}
          onChange={(e) => handleScoringTypeChange(e.target.value as string)}
          loading={isLoadingScoringTypes}
        />
        <TextInputWithIcon
          label='Order'
          placeholder='Order'
          required
          name='order'
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          fullWidth
        />
        {showMaxMarks && (
          <TextInputWithIcon
            label='Max Marks'
            placeholder='Enter Max Marks'
            required
            name='maxMarks'
            value={maxMarks}
            onChange={(e) => setMaxMarks(e.target.value)}
            fullWidth
            type='number'
          />
        )}
      </AddSubSkillFormContainer>
    </DialogModal>
  );
};

export default EditSubSkillModal;
